import { createLogger } from "../../utils/logger"
const logger = createLogger('CadWorkerClient')

/**
 * URL absoluta al bundle compilado del worker. Mismo patrón que con los TTFs en
 * /public/fonts/: webpack.worker.config.js (independiente de webpack.mix.js)
 * compila el worker como bundle self-contained a /public/workers/cad.worker.js
 * y este código lo carga vía URL absoluta usando ASSET_PATH.
 *
 *   - Local      : ASSET_PATH = '/'                       → '/workers/cad.worker.js'
 *   - Producción : ASSET_PATH = 'https://cdn.cloudfront…' → URL absoluta CloudFront
 */
const BASE = (typeof process !== 'undefined' && process.env && process.env.ASSET_PATH
  ? process.env.ASSET_PATH
  : '/').replace(/\/$/, '')
const WORKER_URL = `${BASE}/workers/cad.worker.js`

/**
 * Detecta si una URL es cross-origin respecto al documento actual.
 * En SSR (sin window) asumimos mismo origen.
 */
function _isCrossOrigin(url) {
  if (typeof window === 'undefined') return false
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

export class CadWorkerClient {
  constructor() {
    // Lazy: el Worker se crea en el primer _call().
    //
    // Necesario porque cross-origin Workers (caso producción con CDN) requieren
    // el blob URL workaround — descargar el script vía fetch, hacer Blob, crear
    // el Worker desde URL.createObjectURL (same-origin "blob:"). Aunque el chunk
    // se sirve con CORS correctamente configurado en S3/CloudFront, los browsers
    // siguen rechazando `new Worker(crossOriginUrl)` por restricciones de seguridad
    // adicionales. El fetch SÍ pasa por CORS estándar y eso es suficiente.
    //
    // El chunk emitido por webpack.worker.config.js es self-contained (sin
    // import/export statements), así que ejecutarlo desde blob URL es OK.
    this.worker = null
    this._workerReady = null
    this.seq = 0
    this.pending = new Map()
  }

  async _ensureWorker() {
    if (this._workerReady) return this._workerReady
    this._workerReady = (async () => {
      let w
      if (_isCrossOrigin(WORKER_URL)) {
        // Producción: fetch + blob URL para bypass de restricción cross-origin
        // de Workers (incluso con CORS bien configurado).
        const resp = await fetch(WORKER_URL, { mode: 'cors', credentials: 'omit' })
        if (!resp.ok) {
          throw new Error(`Worker fetch falló: HTTP ${resp.status} en ${WORKER_URL}`)
        }
        const text = await resp.text()
        // Defensa: si el endpoint devuelve HTML (CloudFront SPA fallback), abortar
        // con mensaje útil en vez de pasar HTML a new Worker.
        if (text.trimStart().startsWith('<')) {
          throw new Error(`Worker URL devolvió HTML en lugar de JS: ${WORKER_URL}`)
        }
        const blob = new Blob([text], { type: 'application/javascript' })
        w = new Worker(URL.createObjectURL(blob))
      } else {
        // Local / same-origin: Worker directo, sin overhead.
        w = new Worker(WORKER_URL)
      }
      w.onmessage = (e) => this._onMessage(e)
      w.onerror   = (err) => this._onError(err)
      this.worker = w
      return w
    })()
    return this._workerReady
  }

  /**
  * Procesa entidades del DXF en el worker.
  * Devuelve { totalDesign, dxf } — el dxf actualizado con las entidades
  * clonadas y los nuevos layers EPC. El padre debe reasignar this.dxf
  * con el dxf recibido, porque las mutaciones del worker viajan por
  * structured clone y NO afectan al objeto original en main thread.
  */
  async CreateEntities(dxf, layers, design, onProgress) {
    return this._call('CreateEntities', [dxf, layers, design], onProgress)
  }

  /**
   * Parsea un DXF en el worker (off main thread). Recibe el File; devuelve el
   * cadJson crudo. onProgress recibe {phase:'reading'|'parsing'}.
   */
  async ParseDxf(file, onProgress) {
    return this._call('ParseDxf', [file], onProgress)
  }

  /**
   * Transforma el cadJson procesado en la estructura {entities, layers, origin, bounds}
   * que espera el backend (output de CadSceneBuilder._BuildScene).
   * Se invoca al momento de guardar el pictograma.
   */
  async CreateObject(dxf) {
    return this._call('CreateObject', [dxf])
  }

  async Destroy() {
    // Si quedó una promesa de setup en vuelo, esperarla para terminar limpio
    if (this._workerReady) {
      try { await this._workerReady } catch { /* setup falló, nada que terminar */ }
      this._workerReady = null
    }
    if (!this.worker) return
    // Rechazar peticiones en vuelo para no dejar promesas colgadas
    for (const { reject } of this.pending.values()) {
      reject(new Error('Worker destroyed'))
    }
    this.pending.clear()
    this.worker.terminate()
    this.worker = null
  }

  // ───────────────────────────────────────────── Internos

  async _call(method, args, onProgress) {
    try {
      await this._ensureWorker()
    } catch (err) {
      return Promise.reject(err)
    }
    if (!this.worker) {
      return Promise.reject(new Error('Worker not available'))
    }
    return new Promise((resolve, reject) => {
      const seq = ++this.seq
      this.pending.set(seq, { resolve, reject, onProgress })
      this.worker.postMessage({ seq, method, args })
    })
  }

  _onMessage(event) {
    const { seq, type, value, ok, result, error } = event.data
    const pending = this.pending.get(seq)
    if (!pending) return              // respuesta sin request — ignorar

    // Mensajes intermedios de progreso: invocar callback y NO resolver la promesa.
    // La promesa solo se resuelve cuando llega el mensaje final (type === 'result').
    if (type === 'progress') {
      pending.onProgress?.(value)
      return
    }

    this.pending.delete(seq)
    if (ok) pending.resolve(result)
    else    pending.reject(new Error(error))
  }

  _onError(err) {
    logger.error('worker error:', err)
    for (const { reject } of this.pending.values()) {
      reject(err)
    }
    this.pending.clear()
  }
}
